import { el } from "./dom";
import { i18n, t } from "../i18n";
import { progress } from "../store/progress";
import { settings } from "../store/settings";
import { playerHandle } from "../net/sync";
import { tierSwatch } from "../render/renderer";
import { profileLevel } from "./profile";
import { ICONS } from "./icons";
import { fmt } from "./dom";

/**
 * Shared top bar: optional back button, title, stardust counter, settings gear.
 * Language is changed in Settings, so there is no language toggle here.
 * When `character` is set, the brand is replaced by a tappable character chip
 * (profile avatar + name/code + account level).
 * Returns the element; call `refresh()` to update i18n/stardust.
 */
export function buildTopbar(opts: {
  title?: string;
  onBack?: () => void;
  showStardust?: boolean;
  character?: boolean;
  onCharacterClick?: () => void;
  onSettings?: () => void;
}): { node: HTMLElement; refresh: () => void } {
  const bar = el("div", "topbar");

  const left = el("div", "topbar-left");
  if (opts.onBack) {
    const back = el("button", "icon-btn", "‹");
    back.setAttribute("aria-label", "Back");
    back.onclick = opts.onBack;
    left.appendChild(back);
  }
  let charChip: HTMLElement | undefined;
  if (opts.character) {
    charChip = el("button", "char-chip");
    charChip.setAttribute("aria-label", "Profile");
    charChip.innerHTML = `
      <span class="char-avatar"></span>
      <span class="char-meta">
        <span class="char-name"></span>
        <span class="char-lv"></span>
      </span>`;
    if (opts.onCharacterClick) charChip.onclick = opts.onCharacterClick;
    left.appendChild(charChip);
  } else {
    const titleEl = el("div", "brand");
    if (opts.title) {
      titleEl.innerHTML = `<h1 data-i18n="${opts.title}"></h1>`;
    } else {
      titleEl.innerHTML = `<h1 data-i18n="app.title"></h1><p data-i18n="app.subtitle"></p>`;
    }
    left.appendChild(titleEl);
  }

  const right = el("div", "topbar-right");
  if (opts.showStardust) {
    const sd = el("div", "stardust");
    sd.id = "stardust-counter";
    // On the home (character) bar, keep profile + stardust together on the left
    // and leave only the settings gear on the right.
    (opts.character ? left : right).appendChild(sd);
  }
  if (opts.onSettings) {
    const gear = el("button", "icon-btn icon-svg");
    gear.innerHTML = ICONS.settings;
    gear.setAttribute("aria-label", "Settings");
    gear.onclick = opts.onSettings;
    right.appendChild(gear);
  }

  bar.append(left, right);

  const refresh = (): void => {
    bar.querySelectorAll<HTMLElement>("[data-i18n]").forEach((n) => {
      n.textContent = t(n.dataset.i18n!);
    });
    const sd = bar.querySelector("#stardust-counter");
    if (sd) sd.innerHTML = `<span class="sd-icon">✦</span> ${fmt(progress.snapshot.stardust)}`;
    if (charChip) {
      const avatar = charChip.querySelector(".char-avatar");
      if (avatar) avatar.innerHTML = tierSwatch(profileLevel(), 40);
      const nameEl = charChip.querySelector(".char-name");
      if (nameEl) nameEl.textContent = settings.snapshot.displayName.trim() || playerHandle();
      const lvEl = charChip.querySelector(".char-lv");
      if (lvEl) lvEl.textContent = `Lv ${progress.snapshot.accountLevel}`;
    }
    document.documentElement.lang = i18n.current;
  };
  refresh();
  return { node: bar, refresh };
}
